import React, { useState, useEffect } from "react";
import axios from "axios";
import { Form, Button, Modal, Icon, List } from "semantic-ui-react";

import { DocumentRejections } from "components/views/contracts/contractManagement/DocumentRejections";

import { varietyFormatted } from "./helpers";

import UploadForm from "./UploadForm";

const DocList = ({
  dealerId,
  defaultOpen,
  title,
  docs,
  accepted,
  archived,
  pending,
  expired,
  fetchData,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [activeYear, setActiveYear] = useState(null);
  if (!docs.length) return null;

  const yearButtons = docs
    .filter((f) => f.expiration)
    .map((d) => d.expiration)
    .reduce((acc, cur) => {
      if (!acc.includes(new Date(cur).getFullYear())) {
        acc.push(new Date(cur).getFullYear());
      }

      return acc;
    }, [])
    .sort((a, b) => a - b);

  return (
    <div className="coi-doc-list">
      <h4>
        {!accepted && !pending && (
          <React.Fragment>
            <Button
              icon={isOpen ? "caret down" : "caret right"}
              onClick={() => setOpen(!isOpen)}
              circular
              basic
              size="mini"
              style={{ marginRight: "5px" }}
            />{" "}
          </React.Fragment>
        )}
        {title}{" "}
        {(accepted || pending) && (
          <Icon
            name={accepted ? "check circle" : "clock"}
            color={accepted ? "green" : "yellow"}
          />
        )}
      </h4>
      {(isOpen || defaultOpen) && (
        <div>
          {archived && (
            <React.Fragment>
              <Form>
                <Form.Group>
                  <Form.Radio
                    label="All expiry"
                    checked={activeYear === null}
                    onChange={() => setActiveYear(null)}
                  />
                  {yearButtons.map((y, i) => (
                    <Form.Radio
                      key={i}
                      label={y}
                      checked={activeYear === y}
                      onChange={() => setActiveYear(y)}
                    />
                  ))}
                </Form.Group>
              </Form>
            </React.Fragment>
          )}
          <List
            relaxed
            divided
            verticalAlign="middle"
            style={{ marginTop: "0", maxHeight: "250px", overflowY: "auto" }}
          >
            {docs
              .filter((f) =>
                activeYear
                  ? !f.expiration
                    ? true
                    : new Date(f.expiration).getFullYear() === activeYear
                  : true
              )
              .map((d) => (
                <DocItem
                  key={d.id}
                  accepted={accepted}
                  archived={archived}
                  dealerId={dealerId}
                  expired={expired}
                  expirationDate={d.expiration}
                  filename={d.filename}
                  id={d.id}
                  newImage={d.new_image}
                  pending={pending}
                  rejections={d.rejection_notes}
                  variety={d.variety}
                  fetchData={fetchData}
                />
              ))}
          </List>
        </div>
      )}
    </div>
  );
};

DocList.defaultProps = {
  accepted: false,
  archived: false,
  defaultOpen: false,
  docs: [],
  expired: false,
  pending: false,
  title: "",
};

const DocItem = ({
  id,
  accepted,
  archived,
  dealerId,
  expirationDate,
  expired,
  filename,
  newImage,
  pending,
  rejections,
  variety,
  fetchData,
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  function DeleteButton() {
    const [loading, setLoading] = useState(false);

    function onClick() {
      setLoading(true);

      axios
        .delete(`/images/dealer-coi/${filename}`)
        .then(() => {
          setLoading(false);
          setDeleteConfirm(false);
          fetchData();
        })
        .catch((err) => {
          setLoading(false);
        });
    }

    return (
      <Button {...btnProps} icon="check" onClick={onClick} loading={loading} />
    );
  }

  function RejectionButton() {
    const [isOpen, setOpen] = useState(false);
    if (!rejections.length || newImage) return null;

    function onClose() {
      setOpen(false);
    }

    function onFixedClick(noteId) {
      axios.post(`/rejection-notes/${noteId}/`).then(() => fetchData());
    }

    const hasUnfixed = rejections.find((f) => !f.fixed) ? true : false;

    if (!hasUnfixed) return null;

    return (
      <React.Fragment>
        <Button
          {...btnProps}
          color={hasUnfixed ? "red" : null}
          basic={hasUnfixed ? false : true}
          icon="exclamation"
          onClick={() => setOpen(true)}
          title="View Rejection History"
        />
        <Modal open={isOpen} onClose={onClose}>
          <Modal.Header content={`${varietyFormatted[variety]} Rejections`} />
          <Modal.Content>
            <DocumentRejections
              divider={false}
              rejections={rejections}
              title={`Rejections`}
              // isPartner={isPartner}
              onFixedClick={onFixedClick}
              style={{ marginBottom: "30px" }}
            />
          </Modal.Content>
        </Modal>
      </React.Fragment>
    );
  }

  return (
    <List.Item>
      <List.Content floated="right">
        {!deleteConfirm && (
          <React.Fragment>
            <RejectionButton />
            {filename && (
              <Button
                {...btnProps}
                icon="download"
                as="a"
                download
                href={`/dealer/api/images/dealer-coi/${filename}`}
              />
            )}
            {filename && !archived && (
              <Button
                {...btnProps}
                icon="trash"
                onClick={() => setDeleteConfirm(true)}
              />
            )}
            {pending && !filename && (
              <UploadForm
                dealerId={dealerId}
                variety={variety}
                onSuccess={fetchData}
                documentId={id}
              />
            )}
          </React.Fragment>
        )}
        {deleteConfirm && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <p style={{ margin: "0 10px 0 0" }}>
              Are you sure you want to {pending ? "delete" : "archive"} this?
            </p>
            <div>
              <DeleteButton />
              <Button
                {...btnProps}
                icon="cancel"
                onClick={() => setDeleteConfirm(false)}
              />
            </div>
          </div>
        )}
      </List.Content>
      <List.Content>
        <p>
          <b>{varietyFormatted[variety]}</b>{" "}
          {expirationDate && (
            <React.Fragment>
              {archived && "expiration on"}
              {(pending || accepted) && "expires"}
              {expired && "expired"} {expirationDate}
            </React.Fragment>
          )}
        </p>
      </List.Content>
    </List.Item>
  );
};

DocItem.defaultProps = {
  accepted: false,
  archived: false,
  filename: "",
  expirationDate: "",
  expired: false,
  newImage: false,
  pending: false,
  rejections: [],
  variety: null,
};

const btnProps = {
  size: "mini",
  basic: true,
  circular: true,
};

export default DocList;
